import { type FormEvent, useEffect, useState } from 'react';

import { getPlatformMessageEnhanceSettings, updatePlatformMessageEnhanceSettings } from '../api.js';

export function PlatformMessageEnhance() {
	const [basicPrompt, setBasicPrompt] = useState('');
	const [advancedPrompt, setAdvancedPrompt] = useState('');
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		void getPlatformMessageEnhanceSettings()
			.then((loaded) => {
				if (!current) return;
				setBasicPrompt(loaded.basicPrompt);
				setAdvancedPrompt(loaded.advancedPrompt);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load Message Enhance prompts',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, []);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await updatePlatformMessageEnhanceSettings({
				advancedPrompt,
				basicPrompt,
			});
			setAdvancedPrompt(updated.advancedPrompt);
			setBasicPrompt(updated.basicPrompt);
			setMessage('Message Enhance prompts saved.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save Message Enhance prompts',
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="workspace-section owner-settings-page">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform AI</p>
					<h2>AI Message Enhance</h2>
					<p className="muted">
						Set the platform-wide instructions used by every customer's Basic and
						Advanced message enhancement.
					</p>
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}

			{loading ? (
				<div className="empty-configuration">Loading Message Enhance prompts…</div>
			) : (
				<form className="owner-settings-form" onSubmit={(event) => void save(event)}>
					<section className="settings-card integration-panel owner-ai-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Master prompts</h4>
							<p>
								Set the platform-wide instructions used by the basic and advanced
								enhancement levels.
							</p>
						</div>
						<div className="owner-ai-fields">
							<label className="owner-settings-field">
								<span>Basic prompt</span>
								<textarea
									maxLength={10_000}
									onChange={(event) => {
										setBasicPrompt(event.currentTarget.value);
										setMessage(undefined);
									}}
									required
									rows={10}
									value={basicPrompt}
								/>
							</label>

							<label className="owner-settings-field">
								<span>Advanced prompt</span>
								<textarea
									maxLength={10_000}
									onChange={(event) => {
										setAdvancedPrompt(event.currentTarget.value);
										setMessage(undefined);
									}}
									required
									rows={10}
									value={advancedPrompt}
								/>
							</label>
						</div>
					</section>

					<button className="primary-button owner-settings-save" disabled={busy}>
						{busy ? 'Saving…' : 'Save prompts'}
					</button>
				</form>
			)}
		</section>
	);
}
